// src/contexts/voiceReducer.ts
//
// Voice/Channels finite state machine. See voice-fsm.md (repo root) for the
// full design. The reducer owns: identity, channelsData, prefs (listening +
// selectedTx), ws, tx, rx (per-stream), transcriptions, translation queue,
// and alert exclusive mode. VoiceProvider mirrors useZello/transport state
// into the `ws` and `tx.status` slices since those hooks keep their own
// internal state for the WebSocket lifecycle.

import type { Channel } from 'lib/types/channels'

// ---------- Status enums ----------

export type IdentityStatus = 'idle' | 'valid'
export type ChannelsDataStatus = 'idle' | 'loading' | 'loaded' | 'errored'
export type PrefsStatus = 'idle' | 'hydrating' | 'hydrated'
export type WsStatus = 'idle' | 'connecting' | 'loggedOn' | 'reconnecting' | 'failed' | 'closing'
export type TxStatus = 'idle' | 'starting' | 'sending' | 'busy' | 'error'
export type TranslationStatus = 'idle' | 'playing'
export type ExclusiveStatus = 'normal' | 'exclusive'

// ---------- Sub-shapes ----------

export type RxStream = {
  channel: string
  from: string | null
  codec: string | null
  language: string | null
  startedAt: number
}

export type Transcription = {
  streamId: number
  text: string
  lang: string | null
  final: boolean
}

export type TtsItem = {
  streamId: number
  channel: string
  text: string
  targetLang: string
}

// ---------- Top-level state ----------

export type VoiceState = {
  identity: {
    status: IdentityStatus
    userId: string | null
    orgId: string | null
  }
  channelsData: {
    status: ChannelsDataStatus
    orgId: string | null
    channels: Channel[]
    error: string | null
  }
  prefs: {
    status: PrefsStatus
    listening: string[]
    selectedTx: string | null
  }
  ws: {
    status: WsStatus
    desired: string[]
    loggedOnChannels: string[]
    readyChannels: string[]
    lastError: string | null
  }
  tx: {
    status: TxStatus
    channel: string | null
    streamId: number | null
    error: string | null
  }
  rx: {
    streamsById: Record<number, RxStream>
    channelGain: Record<string, number>
  }
  transcriptions: {
    byChannel: Record<string, Transcription>
  }
  translation: {
    status: TranslationStatus
    currentStreamId: number | null
    queue: TtsItem[]
  }
  exclusive: {
    status: ExclusiveStatus
    channel: string | null
  }
}

export const initialVoiceState: VoiceState = {
  identity: { status: 'idle', userId: null, orgId: null },
  channelsData: { status: 'idle', orgId: null, channels: [], error: null },
  prefs: { status: 'idle', listening: [], selectedTx: null },
  ws: {
    status: 'idle',
    desired: [],
    loggedOnChannels: [],
    readyChannels: [],
    lastError: null,
  },
  tx: { status: 'idle', channel: null, streamId: null, error: null },
  rx: { streamsById: {}, channelGain: {} },
  transcriptions: { byChannel: {} },
  translation: { status: 'idle', currentStreamId: null, queue: [] },
  exclusive: { status: 'normal', channel: null },
}

// ---------- Action union ----------

export type VoiceAction =
  // Identity
  | { type: 'IDENTITY_RESOLVED'; userId: string; orgId: string }
  | { type: 'IDENTITY_CLEARED' }

  // ChannelsData
  | { type: 'CHANNELS_FETCH_STARTED'; fetchOrgId: string }
  | { type: 'CHANNELS_FETCH_OK'; orgId: string; channels: Channel[] }
  | { type: 'CHANNELS_FETCH_ERR'; orgId: string; error: string }
  | { type: 'CHANNELS_CLEARED' }

  // PrefsHydration
  | { type: 'PREFS_HYDRATE_REQUESTED'; contextKey: string }
  | { type: 'PREFS_HYDRATED'; listening: string[]; selectedTx: string | null }
  | { type: 'PREFS_USER_TOGGLED_LISTENING'; channel: string; on: boolean }
  | { type: 'PREFS_USER_SET_TX'; channel: string | null }
  | { type: 'PREFS_USER_LEFT_CHANNEL'; channel: string }
  | { type: 'PREFS_PERSIST_REQUESTED' }

  // WSConnection
  | { type: 'WS_CONNECT_REQUESTED'; channels: string[] }
  | { type: 'WS_OPENED' }
  | { type: 'WS_LOGON_OK'; channels: string[] }
  | { type: 'WS_LOGON_ERR'; error: string }
  | { type: 'WS_CHANNEL_NOT_FOUND'; channel: string }
  | { type: 'WS_CHANNEL_STATUS'; channel: string; status: string }
  | { type: 'WS_WATCHDOG_FIRED' }
  | { type: 'WS_CLOSED'; code: number; reason: string }
  | { type: 'WS_RECONNECT_REQUESTED' }
  | { type: 'WS_DISCONNECT_REQUESTED' }
  | { type: 'WS_KICKED' }

  // TX
  | { type: 'TX_REQUESTED'; channel: string }
  | { type: 'TX_START_STREAM_SENT' }
  | { type: 'TX_START_STREAM_OK'; streamId: number }
  | { type: 'TX_START_STREAM_BUSY' }
  | { type: 'TX_START_STREAM_ERROR'; error: string }
  | { type: 'TX_BUSY_FLASH_EXPIRED' }
  | { type: 'TX_STOPPED' }
  | { type: 'TX_CHANNEL_CHANGED'; channel: string | null }

  // RX
  | {
      type: 'RX_STREAM_STARTED'
      streamId: number
      channel: string
      from: string | null
      codec: string | null
      language: string | null
    }
  | { type: 'RX_STREAM_STOPPED'; streamId: number }
  | { type: 'RX_CHANNEL_VOLUME_SET'; channel: string; gain: number }

  // Transcription
  | { type: 'TRANSCRIPTION_DELTA'; streamId: number; channel: string; text: string; lang: string | null }
  | { type: 'TRANSCRIPTION_FINAL'; streamId: number; channel: string; text: string; lang: string | null }
  | { type: 'TRANSCRIPTION_CLEARED'; channel: string }

  // Translation
  | { type: 'TRANSLATION_AVAILABLE'; streamId: number; channel: string; text: string; targetLang: string }
  | { type: 'TRANSLATION_TTS_STARTED'; streamId: number }
  | { type: 'TRANSLATION_TTS_ENDED'; streamId: number }
  | { type: 'TRANSLATION_QUEUE_CLEARED'; channel: string }

  // AlertExclusiveMode
  | { type: 'EXCLUSIVE_ENTER_REQUESTED'; channel: string }
  | { type: 'EXCLUSIVE_ENTERED'; channel: string }
  | { type: 'EXCLUSIVE_EXIT_REQUESTED' }
  | { type: 'EXCLUSIVE_EXITED' }
  | { type: 'EXCLUSIVE_CHANNEL_NOT_FOUND'; channel: string }

// ---------- Helpers ----------

const lower = (s: string) => s.toLowerCase()
const uniq = (a: string[]) => Array.from(new Set(a))

// ---------- Reducer ----------

export function voiceReducer(state: VoiceState, action: VoiceAction): VoiceState {
  switch (action.type) {
    // ----- Identity -----
    case 'IDENTITY_RESOLVED': {
      const prev = state.identity
      if (prev.status === 'valid' && prev.userId === action.userId && prev.orgId === action.orgId) {
        return state
      }
      // Org/user change wipes downstream slices that were scoped to the old identity.
      return {
        ...initialVoiceState,
        identity: { status: 'valid', userId: action.userId, orgId: action.orgId },
      }
    }

    case 'IDENTITY_CLEARED':
      if (state.identity.status === 'idle') return state
      return initialVoiceState

    // ----- ChannelsData -----
    case 'CHANNELS_FETCH_STARTED':
      return {
        ...state,
        channelsData: {
          status: 'loading',
          orgId: null,
          channels: state.channelsData.channels,
          error: null,
        },
      }

    case 'CHANNELS_FETCH_OK':
      // Drop responses for an org we no longer care about.
      if (state.identity.orgId && action.orgId !== state.identity.orgId) return state
      return {
        ...state,
        channelsData: {
          status: 'loaded',
          orgId: action.orgId,
          channels: action.channels,
          error: null,
        },
      }

    case 'CHANNELS_FETCH_ERR':
      if (state.identity.orgId && action.orgId !== state.identity.orgId) return state
      return {
        ...state,
        channelsData: {
          status: 'errored',
          orgId: null,
          channels: state.channelsData.channels,
          error: action.error,
        },
      }

    case 'CHANNELS_CLEARED':
      return {
        ...state,
        channelsData: { status: 'idle', orgId: null, channels: [], error: null },
      }

    // ----- Prefs -----
    case 'PREFS_HYDRATE_REQUESTED':
      return { ...state, prefs: { ...state.prefs, status: 'hydrating' } }

    case 'PREFS_HYDRATED':
      return {
        ...state,
        prefs: { status: 'hydrated', listening: action.listening, selectedTx: action.selectedTx },
      }

    case 'PREFS_USER_TOGGLED_LISTENING': {
      const key = lower(action.channel)
      const has = state.prefs.listening.some(c => lower(c) === key)
      if (action.on === has) return state
      const listening = action.on
        ? uniq([...state.prefs.listening, action.channel])
        : state.prefs.listening.filter(c => lower(c) !== key)
      return { ...state, prefs: { ...state.prefs, listening } }
    }

    case 'PREFS_USER_SET_TX':
      if (state.prefs.selectedTx === action.channel) return state
      return { ...state, prefs: { ...state.prefs, selectedTx: action.channel } }

    case 'PREFS_USER_LEFT_CHANNEL': {
      const key = lower(action.channel)
      const listening = state.prefs.listening.filter(c => lower(c) !== key)
      const selectedTx =
        state.prefs.selectedTx && lower(state.prefs.selectedTx) === key ? null : state.prefs.selectedTx
      if (listening.length === state.prefs.listening.length && selectedTx === state.prefs.selectedTx) {
        return state
      }
      return { ...state, prefs: { ...state.prefs, listening, selectedTx } }
    }

    case 'PREFS_PERSIST_REQUESTED':
      // Marker action — handled by an effect that watches dispatch timing.
      return state

    // ----- WS -----
    case 'WS_CONNECT_REQUESTED':
      return {
        ...state,
        ws: {
          status: 'connecting',
          desired: uniq(action.channels),
          loggedOnChannels: [],
          readyChannels: [],
          lastError: null,
        },
      }

    case 'WS_OPENED':
      // Socket is open; logon is in flight. Stay in 'connecting' until logon_ok.
      return state

    case 'WS_LOGON_OK':
      return {
        ...state,
        ws: {
          ...state.ws,
          status: 'loggedOn',
          loggedOnChannels: uniq(action.channels),
          lastError: null,
        },
      }

    case 'WS_LOGON_ERR':
      return {
        ...state,
        ws: { ...state.ws, status: 'failed', lastError: action.error },
      }

    case 'WS_CHANNEL_NOT_FOUND': {
      const key = lower(action.channel)
      const desired = state.ws.desired.filter(c => lower(c) !== key)
      const loggedOnChannels = state.ws.loggedOnChannels.filter(c => lower(c) !== key)
      const readyChannels = state.ws.readyChannels.filter(c => lower(c) !== key)
      return { ...state, ws: { ...state.ws, desired, loggedOnChannels, readyChannels } }
    }

    case 'WS_CHANNEL_STATUS': {
      const key = lower(action.channel)
      const online = action.status === 'online'
      const hasReady = state.ws.readyChannels.some(c => lower(c) === key)
      if (online && !hasReady) {
        return { ...state, ws: { ...state.ws, readyChannels: [...state.ws.readyChannels, action.channel] } }
      }
      if (!online && hasReady) {
        return {
          ...state,
          ws: { ...state.ws, readyChannels: state.ws.readyChannels.filter(c => lower(c) !== key) },
        }
      }
      return state
    }

    case 'WS_WATCHDOG_FIRED':
      if (state.ws.status !== 'connecting' && state.ws.status !== 'reconnecting') return state
      return { ...state, ws: { ...state.ws, status: 'failed', lastError: 'watchdog timeout' } }

    case 'WS_CLOSED':
      // Don't clobber 'failed' — that survives the close so the UI keeps the failed banner.
      if (state.ws.status === 'failed') {
        return { ...state, ws: { ...state.ws, loggedOnChannels: [], readyChannels: [] } }
      }
      return {
        ...state,
        ws: { ...state.ws, status: 'idle', loggedOnChannels: [], readyChannels: [] },
      }

    case 'WS_RECONNECT_REQUESTED':
      return {
        ...state,
        ws: { ...state.ws, status: 'reconnecting', lastError: null },
      }

    case 'WS_DISCONNECT_REQUESTED':
      return {
        ...state,
        ws: { status: 'closing', desired: [], loggedOnChannels: [], readyChannels: [], lastError: null },
      }

    case 'WS_KICKED':
      return { ...state, ws: { ...state.ws, status: 'failed', lastError: 'kicked' } }

    // ----- TX -----
    case 'TX_REQUESTED':
      return {
        ...state,
        tx: { status: 'starting', channel: action.channel, streamId: null, error: null },
      }

    case 'TX_START_STREAM_SENT':
      return state

    case 'TX_START_STREAM_OK':
      return {
        ...state,
        tx: { ...state.tx, status: 'sending', streamId: action.streamId, error: null },
      }

    case 'TX_START_STREAM_BUSY':
      return { ...state, tx: { ...state.tx, status: 'busy', streamId: null } }

    case 'TX_START_STREAM_ERROR':
      return { ...state, tx: { ...state.tx, status: 'error', streamId: null, error: action.error } }

    case 'TX_BUSY_FLASH_EXPIRED':
      if (state.tx.status !== 'busy') return state
      return { ...state, tx: { ...state.tx, status: 'idle', streamId: null, error: null } }

    case 'TX_STOPPED':
      if (state.tx.status === 'idle') return state
      return { ...state, tx: { ...state.tx, status: 'idle', streamId: null, error: null } }

    case 'TX_CHANNEL_CHANGED':
      if (state.tx.channel === action.channel) return state
      return { ...state, tx: { ...state.tx, channel: action.channel } }

    // ----- RX -----
    case 'RX_STREAM_STARTED':
      return {
        ...state,
        rx: {
          ...state.rx,
          streamsById: {
            ...state.rx.streamsById,
            [action.streamId]: {
              channel: action.channel,
              from: action.from,
              codec: action.codec,
              language: action.language,
              startedAt: Date.now(),
            },
          },
        },
      }

    case 'RX_STREAM_STOPPED': {
      if (!state.rx.streamsById[action.streamId]) return state
      const { [action.streamId]: _removed, ...rest } = state.rx.streamsById
      return { ...state, rx: { ...state.rx, streamsById: rest } }
    }

    case 'RX_CHANNEL_VOLUME_SET':
      if (state.rx.channelGain[action.channel] === action.gain) return state
      return {
        ...state,
        rx: { ...state.rx, channelGain: { ...state.rx.channelGain, [action.channel]: action.gain } },
      }

    // ----- Transcription -----
    case 'TRANSCRIPTION_DELTA':
    case 'TRANSCRIPTION_FINAL':
      return {
        ...state,
        transcriptions: {
          byChannel: {
            ...state.transcriptions.byChannel,
            [action.channel]: {
              streamId: action.streamId,
              text: action.text,
              lang: action.lang,
              final: action.type === 'TRANSCRIPTION_FINAL',
            },
          },
        },
      }

    case 'TRANSCRIPTION_CLEARED': {
      if (!(action.channel in state.transcriptions.byChannel)) return state
      const { [action.channel]: _gone, ...rest } = state.transcriptions.byChannel
      return { ...state, transcriptions: { byChannel: rest } }
    }

    // ----- Translation -----
    case 'TRANSLATION_AVAILABLE':
      return {
        ...state,
        translation: {
          ...state.translation,
          queue: [
            ...state.translation.queue,
            { streamId: action.streamId, channel: action.channel, text: action.text, targetLang: action.targetLang },
          ],
        },
      }

    case 'TRANSLATION_TTS_STARTED':
      return {
        ...state,
        translation: {
          status: 'playing',
          currentStreamId: action.streamId,
          queue: state.translation.queue.filter(t => t.streamId !== action.streamId),
        },
      }

    case 'TRANSLATION_TTS_ENDED':
      if (state.translation.currentStreamId !== action.streamId && state.translation.status === 'idle') {
        return state
      }
      return { ...state, translation: { ...state.translation, status: 'idle', currentStreamId: null } }

    case 'TRANSLATION_QUEUE_CLEARED':
      return {
        ...state,
        translation: {
          ...state.translation,
          queue: state.translation.queue.filter(t => t.channel !== action.channel),
        },
      }

    // ----- Exclusive -----
    case 'EXCLUSIVE_ENTER_REQUESTED':
    case 'EXCLUSIVE_EXIT_REQUESTED':
      return state

    case 'EXCLUSIVE_ENTERED':
      return { ...state, exclusive: { status: 'exclusive', channel: action.channel } }

    case 'EXCLUSIVE_EXITED':
      return { ...state, exclusive: { status: 'normal', channel: null } }

    case 'EXCLUSIVE_CHANNEL_NOT_FOUND':
      if (state.exclusive.status === 'normal') return state
      return { ...state, exclusive: { status: 'normal', channel: null } }

    default: {
      const _exhaustive: never = action
      void _exhaustive
      return state
    }
  }
}

// ---------- Selectors ----------

export const selectContextKey = (s: VoiceState): string | null =>
  s.identity.status === 'valid' && s.identity.userId && s.identity.orgId
    ? `${s.identity.userId}:${s.identity.orgId}`
    : null

export const selectChannelsTrusted = (s: VoiceState): boolean =>
  s.channelsData.status === 'loaded' &&
  s.channelsData.orgId !== null &&
  s.channelsData.orgId === s.identity.orgId

export const selectReadyChannels = (s: VoiceState): string[] =>
  s.ws.readyChannels.filter(c => s.ws.loggedOnChannels.some(l => l.toLowerCase() === c.toLowerCase()))

export const selectSpeakingChannels = (s: VoiceState): string[] =>
  Array.from(new Set(Object.values(s.rx.streamsById).map(st => st.channel)))

export const selectIsForeignSpeaking = (s: VoiceState, channel: string, selfUsername: string | null): boolean => {
  const key = channel.toLowerCase()
  return Object.values(s.rx.streamsById).some(
    st => st.channel.toLowerCase() === key && st.from !== selfUsername,
  )
}

export const selectListenerLoadingChannels = (s: VoiceState): string[] => {
  const ready = new Set(s.ws.readyChannels.map(c => c.toLowerCase()))
  return s.ws.desired.filter(c => !ready.has(c.toLowerCase()))
}

export const selectRxDuckGain = (s: VoiceState): number =>
  s.translation.status === 'playing' ? 0.2 : 1.0

export const selectPttDisplayLabel = (s: VoiceState): string => {
  if (s.ws.status === 'failed') return 'Reconnect'
  if (s.ws.status === 'connecting' || s.ws.status === 'reconnecting') return 'Connecting…'
  if (s.tx.status === 'busy') return 'Busy'
  if (s.tx.status === 'sending') return 'Talking'
  return 'Hold to talk'
}

export const selectPttDisabled = (s: VoiceState, selfUsername: string | null): boolean => {
  if (s.ws.status !== 'loggedOn') return true
  if (s.tx.status !== 'idle') return true
  if (!s.tx.channel) return true
  if (!selectReadyChannels(s).some(c => c.toLowerCase() === s.tx.channel!.toLowerCase())) return true
  if (selectIsForeignSpeaking(s, s.tx.channel, selfUsername)) return true
  return false
}
